import React, {
  ComponentProps,
  useEffect,
  useMemo,
  useState,
  MouseEvent,
  KeyboardEvent,
} from 'react';
import { css } from 'styled-components';
import { RadixSelect } from 'components/common/RadixSelect';
import { useTranslation } from 'react-i18next';
import { tabletBreakpoint, useIsMobile } from 'WindowDimensionProvider';
import { CollapsibleCard } from 'components/CollapsibleCard';
import {
  Flex,
  HidableResponsiveContentWrapper,
  Sep,
  SrOnly,
} from 'components/common/styled/common-styled';
import { currencyFormat, isImageRatioGreaterOne } from 'utils';
import { useBrandCurrency } from 'hooks/useBrandCurrency';
import { TruncatedText, TruncationMode } from 'components/TruncatedText';
import { SessionTimeOption } from 'features/Reserve/hooks/useSessionTimeOptions';
import {
  ResourceHeading2,
  ResourceParagraph,
} from 'components/theme/Typography/ResourceTypography';
import { Session } from 'types/listings';
import { ListingPictureKind, MediaV3, ResourceGroupV3 } from '@kouto/types';
import { getOriginalMediaLink } from 'utils/listings';
import * as Styled from './styles';
import { PriceTierPopover } from '../Booking/PriceTier';
import { PriceTierWithParticipants } from '../Booking/PriceTier/types';

export interface SessionDateAndDuration {
  startDateTime: string;
  duration: number;
}

interface CollapsibleResourceProps {
  group: ResourceGroupV3;
  title?: string | null;
  allSessionTimes: SessionTimeOption[];
  initialPrice: string;
  capacityLabel: string;
  sessions: Session[];
  description?: string | null;
  includedItems: Array<string>;
  resourceImage: MediaV3[];
  onReserveClick: (session: SessionDateAndDuration) => void;
  onCardClick?: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  showTruncatedView?: boolean;
  handleGroupSelect: (groupId: string) => void;
  priceTiers: PriceTierWithParticipants[];
  onParticipantsChange: ComponentProps<typeof PriceTierPopover>['onChange'];
}

const headerStyles = css`
  padding: 20px;

  @media all and (max-width: ${tabletBreakpoint}) {
    padding: 12px;
  }
`;

const CollapsibleResource = ({
  title,
  initialPrice,
  group,
  allSessionTimes,
  capacityLabel,
  sessions,
  description,
  includedItems,
  resourceImage,
  onReserveClick,
  onCardClick,
  onMouseEnter,
  onMouseLeave,
  handleGroupSelect,
  showTruncatedView = false,
  priceTiers,
  onParticipantsChange,
}: CollapsibleResourceProps) => {
  const isMobileView = useIsMobile();
  const currency = useBrandCurrency();
  const { t: translate } = useTranslation();
  const [isSixteenNineRatio, setIsSixteenNineRatio] = useState(false);

  const onImgLoad: React.ReactEventHandler<HTMLImageElement> = (e) => {
    if (e.target instanceof HTMLImageElement) {
      setIsSixteenNineRatio(
        isImageRatioGreaterOne(e.target.naturalWidth, e.target.naturalHeight),
      );
    }
  };

  const handleOnReserveClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (selectedSession) {
      const { startDateTime, duration } = selectedSession;
      onReserveClick({
        startDateTime,
        duration,
      });
    }
  };

  const [selectedSession, setSelectedSession] = useState<Session | undefined>();

  const resourceImageMedia = resourceImage.find(
    (media) => media.kind === ListingPictureKind.GALLERY,
  );
  const resourceImageUrl = resourceImageMedia
    ? getOriginalMediaLink(resourceImageMedia)
    : undefined;

  useEffect(() => {
    if (allSessionTimes.length > 0 && !selectedSession) {
      setSelectedSession(sessions[0]);
    }
  }, [sessions, selectedSession, allSessionTimes]);

  const maxParticipants = useMemo(() => {
    if (selectedSession) {
      return sessions.filter(
        (session) =>
          selectedSession.startDateTime === session.startDateTime &&
          selectedSession.duration === session.duration,
      ).length;
    }
    return 0;
  }, [sessions, selectedSession]);

  const selectedSessionOption = useMemo(() => {
    return allSessionTimes.find((timeOption) => {
      const [sessionTime, sessionDuration] = timeOption.value.split('-');
      return (
        selectedSession?.startDateTime.split('T')[1] === sessionTime &&
        selectedSession?.duration === parseInt(sessionDuration, 10)
      );
    });
  }, [allSessionTimes, selectedSession]);

  const onSessionChange = (time: string, duration: number) => {
    const session = sessions.find(
      (s) => s.startDateTime.split('T')[1] === time && s.duration === duration,
    );
    if (session) {
      setSelectedSession(session);
    }
  };

  const disabled = !allSessionTimes.length;

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    (e.currentTarget.firstElementChild as HTMLElement | null)?.click();
  };

  return (
    <CollapsibleCard
      aria-label={translate('selectData', { data: title })}
      containerStyles={
        isMobileView
          ? mobileContainerStyles(showTruncatedView)
          : desktopContainerStyles()
      }
      customHeaderClickAction={(onOpenAccordion) => {
        if (onCardClick) {
          onCardClick();
        } else {
          onOpenAccordion();
        }
      }}
      headerStyles={headerStyles}
      onKeyDown={handleKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      renderHeader={() => (
        <Styled.HeaderWrapper gap="24px" mobileGap="18px">
          <Styled.ResourceGroupImage
            className="resource-group-card__resource-image mapped-collection-collapsible-resource-image"
            src={resourceImageUrl}
            alt={resourceImageMedia?.altText || title || undefined}
            isSixteenNineRatio={isSixteenNineRatio}
            onLoad={onImgLoad}
          />
          <Flex
            flex={1}
            justifyContent="space-between"
            alignItem={
              isMobileView || showTruncatedView ? 'flex-start' : 'center'
            }
            flexWrap="wrap"
            gap={isMobileView ? 6 : 8}
            width="100%"
            direction={isMobileView || showTruncatedView ? 'column' : 'row'}
          >
            <Styled.MainHeading
              mobileFontSize="16px"
              mobileFontWeight="normal"
              fontWeight="700"
              className="resource-group-card__resource-title"
            >
              {title}
            </Styled.MainHeading>

            <Flex
              direction="row"
              isContent={!isMobileView && !showTruncatedView}
              flex={1}
              gap={isMobileView || showTruncatedView ? 4 : 12}
              flexWrap="wrap"
              justifyContent={
                isMobileView || showTruncatedView
                  ? 'flex-start'
                  : 'space-around'
              }
            >
              {currency && (initialPrice !== '' || !allSessionTimes.length) && (
                <Styled.ResourceGroupText
                  mobileFontSize="12px"
                  lineHeight="normal"
                  className="resource-group-card__resource-price"
                >
                  {!allSessionTimes.length
                    ? translate('unavailable')
                    : `${translate('from')} ${currencyFormat(currency)(
                        initialPrice,
                      )}`}
                </Styled.ResourceGroupText>
              )}
              {capacityLabel && (isMobileView || showTruncatedView) ? (
                <Styled.ResourceGroupText
                  lineHeight="normal"
                  className="resource-group-card__resource-capacity-bullet"
                >
                  •
                </Styled.ResourceGroupText>
              ) : null}
              {!!capacityLabel && (
                <Styled.ResourceGroupText
                  mobileFontSize="12px"
                  lineHeight="normal"
                  className="resource-group-card__capacity-label"
                >
                  {capacityLabel}
                </Styled.ResourceGroupText>
              )}
            </Flex>

            {(isMobileView || !showTruncatedView) && (
              <div>
                {showTruncatedView && isMobileView && (
                  <Styled.ReserveButton
                    onClick={() => handleGroupSelect(group.id)}
                    type="button"
                    disabled={disabled}
                    className="resource-group-card__resource-book-btn"
                  >
                    {translate('reserveNow')}
                  </Styled.ReserveButton>
                )}

                {!showTruncatedView && (
                  <HidableResponsiveContentWrapper maxWidth={512}>
                    <Styled.ButtonWrapper>
                      {!isMobileView && (
                        <Styled.SelectBox onClick={(e) => e.stopPropagation()}>
                          <SrOnly>{translate('selectSession')}</SrOnly>
                          <RadixSelect
                            aria-label={translate('selectSession')}
                            options={allSessionTimes}
                            value={selectedSessionOption?.value || ''}
                            placeholder="Select"
                            onValueChange={(value) => {
                              const option = allSessionTimes.find(
                                (opt) => opt.value === value,
                              );
                              if (option) {
                                const [sessionTime, sessionDuration] =
                                  option.value.split('-');
                                onSessionChange(
                                  sessionTime,
                                  parseInt(sessionDuration, 10),
                                );
                              }
                            }}
                            className="resource-group-card__resource-sessions-time"
                          />
                        </Styled.SelectBox>
                      )}

                      {currency?.code && !isMobileView ? (
                        <PriceTierPopover
                          asChild
                          priceTiers={priceTiers}
                          maxParticipants={maxParticipants}
                          currency={currency.code}
                          onChange={onParticipantsChange}
                          onSubmit={handleOnReserveClick}
                        >
                          <Styled.ReserveButton
                            type="button"
                            disabled={disabled}
                            className="resource-group-card__resource-book-btn"
                          >
                            {translate('reserve')}
                          </Styled.ReserveButton>
                        </PriceTierPopover>
                      ) : null}
                    </Styled.ButtonWrapper>
                  </HidableResponsiveContentWrapper>
                )}
              </div>
            )}
          </Flex>
        </Styled.HeaderWrapper>
      )}
      role="button"
      tabIndex={0}
    >
      <Styled.DetailsContainer>
        <Sep />
        <Styled.ResourceDetailsSection
          gap={16}
          direction="column"
          alignItem="flex-start"
          className="resource-group-card__resource-details-section"
        >
          <ResourceHeading2
            className="resource-group-card__resource-details-title"
            fontWeight="350"
          >
            {translate('details')}
          </ResourceHeading2>
          <TruncatedText
            isRichText
            text={description}
            maxLines={2}
            mode={TruncationMode.INLINE}
            customStyles={resourceGroupTextStyles}
          />
        </Styled.ResourceDetailsSection>

        {includedItems?.length >= 1 && (
          <>
            <Sep />
            <Styled.ResourceIncludedSection
              gap={16}
              direction="column"
              alignItem="flex-start"
            >
              <ResourceHeading2
                className="resource-group-card__resource-included-title"
                fontWeight="350"
              >
                {translate('whatsIncluded')}
              </ResourceHeading2>
              <Flex
                direction="row"
                alignItem="center"
                gap={8}
                flexWrap="wrap"
                className="resource-group-card__resource-included-items-content"
              >
                {includedItems.map((item) => (
                  <Styled.ResourceIncludedItem
                    className="resource-group-card__resource-included-item-wrapper"
                    key={item}
                  >
                    <ResourceParagraph
                      fontSize="14px"
                      className="resource-group-card__resource-included-item"
                    >
                      {item}
                    </ResourceParagraph>
                  </Styled.ResourceIncludedItem>
                ))}
              </Flex>
            </Styled.ResourceIncludedSection>
          </>
        )}
      </Styled.DetailsContainer>
    </CollapsibleCard>
  );
};

const containerStyles = () => css`
  border: 0.5px solid var(--way-colors-borderColor);
  box-shadow: var(--way-design-boxShadow-s);
  background-color: transparent;
`;

const mobileContainerStyles = (isMapped: boolean) => css`
  ${containerStyles()};
  ${isMapped
    ? `
  border: transparent;
  box-shadow: none;
    `
    : ''}
`;

const desktopContainerStyles = () => css`
  ${containerStyles()};
`;

const resourceGroupTextStyles = css`
  margin-bottom: 0;
  & > p {
    font-family: ${({ theme }) => theme.font.primaryFont};
    margin: 0px;
    padding: 0px;
    font-style: normal;
    font-weight: 400;
    line-height: 23px;
    font-size: 14px;
    color: var(--way-colors-contrastColorShades-80);
  }
`;

export default CollapsibleResource;
